import { getActivityModule } from "../../../modules/cmoe/activityModuleRegistry";
import { ACTIVITY_COMMAND_TYPES } from "../../../domain/cmoe/activity/activityCommandTypes";
import { activityDocumentToLivePayload } from "../../../domain/cmoe/activity/activityLivePayloadMapper";
import {
  fetchActivitySnapshot,
  saveActivitySnapshot,
  subscribeActivitySnapshot,
} from "../../../repositories/cmoe/firestoreActivityRepository";
import { isActivityRuntimePrimary } from "../../../config/cmoeOrchestrationMode";
import { mirrorActivityToV1LiveMatch } from "../compatibility/v1LiveMatchMirror";

export { isActivityRuntimePrimary };

function newActivityCommandId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `act_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function moduleFromActivityDoc(activityDoc) {
  const ref = activityDoc?.activityModuleRef;
  if (!ref?.moduleId || !ref?.moduleVersion) {
    return getActivityModule("badminton.rally", "1");
  }
  return getActivityModule(ref.moduleId, ref.moduleVersion);
}

function snapshotFromActivityDoc(activityDoc) {
  const mod = moduleFromActivityDoc(activityDoc);
  if (!mod) return null;
  const rawPayload = activityDoc?.payload;
  const snap = mod.deserialize(rawPayload);
  if (!snap) return null;
  return {
    ...snap,
    activityVersion: activityDoc.activityVersion ?? snap.activityVersion,
    eventSeq: activityDoc.eventSeq ?? snap.eventSeq,
    payloadSchemaVersion: activityDoc.payloadSchemaVersion ?? snap.payloadSchemaVersion,
    lastCommandId: activityDoc.lastCommandId ?? snap.lastCommandId,
  };
}

/**
 * @param {string} matchId room session match id
 * @param {string} roomCode
 * @param {Record<string, unknown>} livePayload
 * @param {string} managingUid
 * @param {string} [commandId]
 */
export async function applyActivityLiveState(matchId, roomCode, livePayload, managingUid, commandId) {
  const activityDoc = await fetchActivitySnapshot(matchId);
  const mod = moduleFromActivityDoc(activityDoc || {});
  if (!mod) {
    throw new Error("activity_runtime: module not found");
  }

  let snap = snapshotFromActivityDoc(activityDoc) || mod.createInitialState({ bindings: {} });

  const cmd = {
    commandId: commandId || newActivityCommandId(),
    matchId,
    type: ACTIVITY_COMMAND_TYPES.SYNC_LIVE_STATE,
    payload: { live: livePayload },
    baseActivityVersion: snap.activityVersion,
    baseLeaseEpoch: 0,
  };

  let result = mod.handleCommand({ snapshot: snap, leaseEpoch: 0 }, cmd);
  if (result.status === "conflict") {
    const fresh = await fetchActivitySnapshot(matchId);
    snap = snapshotFromActivityDoc(fresh) || snap;
    result = mod.handleCommand(
      { snapshot: snap, leaseEpoch: 0 },
      { ...cmd, baseActivityVersion: snap.activityVersion }
    );
  }
  if (result.status !== "success") {
    return { ok: false, code: result.status === "reject" ? result.code : "CONFLICT" };
  }

  const next = result.snapshot;
  const modRef = activityDoc?.activityModuleRef || {
    moduleId: mod.moduleId,
    moduleVersion: mod.moduleVersion,
  };

  await saveActivitySnapshot(matchId, {
    roomCode,
    activityModuleRef: modRef,
    activityVersion: next.activityVersion,
    eventSeq: next.eventSeq,
    payloadSchemaVersion: next.payloadSchemaVersion,
    lastCommandId: next.lastCommandId,
    payload: mod.serialize(next),
  });

  const mirrored = activityDocumentToLivePayload({
    payload: next.payload,
    activityVersion: next.activityVersion,
    lastCommandId: next.lastCommandId,
  });
  if (mirrored && managingUid) {
    await mirrorActivityToV1LiveMatch({
      roomCode,
      roomMatchId: matchId,
      managingUid,
      livePayload: mirrored,
    });
  }

  return { ok: true, commandId: cmd.commandId };
}

/**
 * @param {string} matchId
 * @param {(data: object | null) => void} onData
 */
export function subscribeActivityRuntime(matchId, onData) {
  return subscribeActivitySnapshot(matchId, (doc) => {
    onData(activityDocumentToLivePayload(doc));
  });
}

export async function patchActivityOrchestrationPause(matchId, roomCode, paused) {
  const activityDoc = await fetchActivitySnapshot(matchId);
  if (!activityDoc) return { ok: true, skipped: true };
  const mod = moduleFromActivityDoc(activityDoc);
  if (!mod) return { ok: false, code: "NO_MODULE" };
  let snap = snapshotFromActivityDoc(activityDoc) || mod.createInitialState({ bindings: {} });
  const cmd = {
    commandId: newActivityCommandId(),
    matchId,
    type: ACTIVITY_COMMAND_TYPES.SET_ORCHESTRATION_PAUSE,
    payload: { paused },
    baseActivityVersion: snap.activityVersion,
    baseLeaseEpoch: 0,
  };
  const result = mod.handleCommand({ snapshot: snap, leaseEpoch: 0 }, cmd);
  if (result.status !== "success") return { ok: false, code: "COMMAND_FAILED" };
  const next = result.snapshot;
  const modRef = activityDoc?.activityModuleRef || {
    moduleId: mod.moduleId,
    moduleVersion: mod.moduleVersion,
  };
  await saveActivitySnapshot(matchId, {
    roomCode,
    activityModuleRef: modRef,
    activityVersion: next.activityVersion,
    eventSeq: next.eventSeq,
    payloadSchemaVersion: next.payloadSchemaVersion,
    lastCommandId: next.lastCommandId,
    payload: mod.serialize(next),
  });
  return { ok: true };
}
